import { getApiErrorMessage, unwrapApiData } from '@/shared/utils/apiResponse';
import { normalizeCpfCnpj } from '@/shared/utils/documentValidation';
import { apiGet, apiPatch, apiPost } from '@/shared/utils/requestBuilder';

import type {
  BuscaContextoResultado,
  CampoParametroSelf,
  ConsultaCnpjNegociacaoSelfResultado,
  ConsultaPagamentoResultado,
  CriarAgentePayload,
  CriarAgenteResultado,
  IniciarPagamentoPayload,
  IniciarPagamentoResultado,
  ObjetivoOpcao,
  ObjetivoSelf,
  PerfilSelf,
  SimulacaoPayload,
} from '../types';

const SKIP_AUTH = true;

const garantirSucesso = async (response: Response, fallback: string): Promise<void> => {
  if (!response.ok) {
    if (response.status >= 500) throw new Error(fallback);
    const payload: unknown = await response.json().catch(() => null);
    throw new Error(getApiErrorMessage(payload, fallback));
  }
};

const unwrapData = <T>(response: Response, fallback: string): Promise<T> =>
  unwrapApiData<T>(response, { fallback, source: 'negociacaoSelf.services' });

export const negociacaoSelfService = {
  async carregarObjetivos(): Promise<ObjetivoOpcao[]> {
    const response = await apiGet('/negociacao-self/objetivos', undefined, SKIP_AUTH);
    const data = await unwrapData<{ objetivos: ObjetivoOpcao[] }>(
      response,
      'Erro ao carregar os objetivos',
    );
    return data.objetivos;
  },

  async consultarCnpj(
    cnpj: string,
    signal?: AbortSignal,
  ): Promise<ConsultaCnpjNegociacaoSelfResultado> {
    const response = await apiPost(
      '/negociacao-self/cnpj',
      { cnpj: normalizeCpfCnpj(cnpj) },
      SKIP_AUTH,
      signal,
    );
    return unwrapData<ConsultaCnpjNegociacaoSelfResultado>(response, 'Erro ao consultar o CNPJ');
  },

  async buscarContextoEmpresa(perfil: PerfilSelf): Promise<BuscaContextoResultado> {
    const response = await apiPost(
      '/negociacao-self/leads',
      {
        nome: perfil.nome,
        email: perfil.email,
        cnpj: normalizeCpfCnpj(perfil.cnpj),
        razaoSocial: perfil.razaoSocial,
        nomeFantasia: perfil.nomeFantasia,
        site: perfil.site,
      },
      SKIP_AUTH,
    );
    return unwrapData<BuscaContextoResultado>(response, 'Erro ao buscar contexto da empresa');
  },

  async carregarCamposObjetivo(objetivo: ObjetivoSelf): Promise<CampoParametroSelf[]> {
    const response = await apiGet(
      `/negociacao-self/objetivos/${objetivo}/campos`,
      undefined,
      SKIP_AUTH,
    );
    const data = await unwrapData<{ campos: CampoParametroSelf[] }>(
      response,
      'Erro ao carregar os parâmetros do objetivo',
    );
    return data.campos;
  },

  async criarAgente(
    payload: CriarAgentePayload,
    signal?: AbortSignal,
  ): Promise<CriarAgenteResultado> {
    const { leadId, accessToken, ...parametros } = payload;

    const salvarResponse = await apiPatch(
      `/negociacao-self/leads/${leadId}`,
      { accessToken, ...parametros },
      SKIP_AUTH,
      signal,
    );
    await garantirSucesso(salvarResponse, 'Erro ao salvar as configurações');

    if (signal?.aborted) {
      throw new DOMException('Operação cancelada', 'AbortError');
    }

    const agenteResponse = await apiPost(
      `/negociacao-self/leads/${leadId}/agente`,
      { accessToken },
      SKIP_AUTH,
      signal,
    );
    return unwrapData<CriarAgenteResultado>(agenteResponse, 'Erro ao criar o agente');
  },

  async responderSimulacao(payload: SimulacaoPayload): Promise<string> {
    const { leadId, ...body } = payload;
    const response = await apiPost(`/negociacao-self/leads/${leadId}/simulacao`, body, SKIP_AUTH);
    const data = await unwrapData<{ resposta: string }>(response, 'Erro ao simular a resposta');
    return data.resposta;
  },

  async iniciarPagamento(payload: IniciarPagamentoPayload): Promise<IniciarPagamentoResultado> {
    const { leadId, ...body } = payload;
    const response = await apiPost(`/negociacao-self/leads/${leadId}/pagamento`, body, SKIP_AUTH);
    return unwrapData<IniciarPagamentoResultado>(response, 'Erro ao iniciar o pagamento');
  },

  async consultarPagamento(
    payload: {
      leadId: string;
      accessToken: string;
    },
    signal?: AbortSignal,
  ): Promise<ConsultaPagamentoResultado> {
    const response = await apiPost(
      `/negociacao-self/leads/${payload.leadId}/pagamento/consulta`,
      { accessToken: payload.accessToken },
      SKIP_AUTH,
      signal,
    );
    return unwrapData<ConsultaPagamentoResultado>(response, 'Erro ao consultar o pagamento');
  },
};
